import type { ImageProps } from 'expo-image';

import {
  primum_logo_light,
  primum_logo_light_ribbon,
  primum_logo_mark,
  primum_logo_mark_2x,
  primum_logo_mark_reverse_2x,
  primum_logo_mark_reverse_ribbon_2x,
  primum_logo_mark_ribbon,
  primum_logo_mark_ribbon_2x,
  primum_mark,
  primum_mark_ribbon,
} from '@/assets/images';
import { isAwarenessRibbonActive } from '@/constants/awarenessRibbon';

/**
 * The logo images the app draws, by how they are made. `reverse` and `light` are the white logo, for
 * green backgrounds. `dark2x` and `dark` are the dark green logo, for white backgrounds, in two
 * greens: `dark2x` is the darker one the web screens use and `dark` is the brand green. `mark` is the
 * circle and arcs alone. Each ribbon image is its plain image with the ribbon laid over it, so the
 * logo itself is unchanged. The ones with `2x` in their name are one file and the others come in
 * three densities.
 */
export type AwarenessLogoKind = 'reverse' | 'light' | 'dark2x' | 'dark' | 'mark';

type LogoSource = ImageProps['source'];

/**
 * Each kind with its plain image, its ribbon image, and the width over height of both. The ribbon
 * image is taller than the plain one: its canvas has room for the tails, which hang below the
 * logo. Both ratios come from the image files.
 */
const LOGOS: Record<
  AwarenessLogoKind,
  { plain: LogoSource; ribbon: LogoSource; plainRatio: number; ribbonRatio: number }
> = {
  reverse: {
    plain: primum_logo_mark_reverse_2x,
    ribbon: primum_logo_mark_reverse_ribbon_2x,
    plainRatio: 505 / 63,
    ribbonRatio: 505 / 82,
  },
  light: {
    plain: primum_logo_light,
    ribbon: primum_logo_light_ribbon,
    plainRatio: 194 / 24,
    ribbonRatio: 194 / 31,
  },
  dark2x: {
    plain: primum_logo_mark_2x,
    ribbon: primum_logo_mark_ribbon_2x,
    plainRatio: 505 / 63,
    ribbonRatio: 505 / 82,
  },
  dark: {
    plain: primum_logo_mark,
    ribbon: primum_logo_mark_ribbon,
    plainRatio: 193 / 24,
    ribbonRatio: 193 / 31,
  },
  mark: {
    plain: primum_mark,
    ribbon: primum_mark_ribbon,
    plainRatio: 241 / 128,
    ribbonRatio: 241 / 172,
  },
};

export type LogoBox = {
  height: number;
  width: number;
  marginBottom?: number;
  marginVertical?: number;
  alignSelf?: 'center';
};

/**
 * The image and box to draw for a logo while the awareness ribbon runs, or the plain ones after.
 *
 * The ribbon image is taller than the plain logo for the same width. Inside the plain logo's box it
 * would be scaled down to fit the extra height, so the box grows just enough to keep the logo at the
 * size it had, and a negative bottom margin gives that height back to the layout. The tails then
 * fall into whatever space was already below the logo. A box that already has room, because the
 * plain logo was limited by its width, is left alone.
 */
export function awarenessLogo(
  kind: AwarenessLogoKind,
  box: LogoBox,
  now: Date = new Date()
): { source: LogoSource; style: LogoBox } {
  const logo = LOGOS[kind];

  if (!isAwarenessRibbonActive(now)) {
    return { source: logo.plain, style: box };
  }

  const drawnWidth = Math.min(box.width, box.height * logo.plainRatio);

  const neededHeight = Math.ceil(drawnWidth / logo.ribbonRatio);

  if (neededHeight <= box.height) {
    return { source: logo.ribbon, style: box };
  }

  const marginBottom = box.marginBottom ?? box.marginVertical ?? 0;

  return {
    source: logo.ribbon,
    style: {
      ...box,
      height: neededHeight,
      marginBottom: marginBottom - (neededHeight - box.height),
    },
  };
}
